import { Dialog, DialogPanel } from '@headlessui/react'
import { Bars3Icon, UserIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { LogOutIcon, Search } from 'lucide-react'
import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import { LogoIcon } from '@/assets/icons'
import { Button } from '@/components/ui/button'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useAppDispatch, useAppSelector } from '@/hooks'
import { cn } from '@/lib/utils'
import { logout } from '@/slices/auth'

export default function Header({ currentPage }: { currentPage: string }) {
	const navigate = useNavigate()
	const dispatch = useAppDispatch()
	const auth = useAppSelector(state => state.auth)

	const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

	return (
		<header>
			<nav
				aria-label="Global"
				className="mx-auto flex max-w-8xl items-center justify-between p-6 lg:px-8">
				<div className="max-lg:gap-4 lg:flex-1 items-center lg:justify-between flex">
					<div className="flex lg:flex-1 max-lg:order-2">
						<a href="/" className="-m-1.5 p-1.5">
							<span className="sr-only">Book Stands</span>
							<LogoIcon className="max-lg:size-6" />
						</a>
					</div>
					<div className="flex lg:hidden max-lg:order-1">
						<button
							type="button"
							onClick={() => setMobileMenuOpen(true)}
							className="-m-2.5 size-8 inline-flex items-center justify-center rounded-md border border-[#EEEFF0] text-secondary">
							<span className="sr-only">Open main menu</span>
							<Bars3Icon aria-hidden="true" className="size-6" />
						</button>
					</div>
				</div>
				{auth.type ? (
					<div className="flex lg:hidden items-center gap-2">
						<div
							onClick={() => dispatch(logout())}
							className="border cursor-pointer border-[#EEEFF0] size-8 flex items-center justify-center rounded-xl">
							<LogOutIcon className="size-4 shrink-0 rotate-180" />
						</div>
						<div
							onClick={() =>
								navigate(auth.type === 'admin' ? '/admin-panel' : '/customer/overview')
							}
							className="border cursor-pointer border-[#EEEFF0] size-8 flex items-center justify-center rounded-xl">
							<UserIcon className="size-4 shrink-0" />
						</div>
					</div>
				) : (
					<Button onClick={() => navigate('/login')} size="sm" className="text-xs h-6 lg:hidden">
						Anmelden
					</Button>
				)}
				<Tabs defaultValue={currentPage} className="hidden w-[575px] lg:flex lg:gap-x-14">
					<TabsList className="grid h-auto rounded-[48px] w-full grid-cols-5">
						<TabsTrigger
							onClick={() => navigate('/')}
							value="startseite"
							className="cursor-pointer rounded-[48px] data-[state=active]:text-primary data-[state=active]:shadow-none font-extrabold py-[17px] px-5">
							Startseite
						</TabsTrigger>
						<TabsTrigger
							value="regalMieten"
							onClick={() => {
								window.location.href = '/customer/regal-mieten'
							}}
							className="cursor-pointer rounded-[48px] data-[state=active]:text-primary data-[state=active]:shadow-none font-extrabold py-[17px] px-5">
							Regal Mieten
						</TabsTrigger>
						<TabsTrigger
							value="search"
							onClick={() => navigate('/suchen')}
							className="cursor-pointer rounded-[48px] data-[state=active]:text-primary data-[state=active]:shadow-none font-extrabold py-[17px] px-5">
							<Search />
							Artikelsuche
						</TabsTrigger>
						<TabsTrigger
							value="minmini"
							onClick={() => window.open('https://www.minmini-shop.de/')}
							className="cursor-pointer rounded-[48px] data-[state=active]:text-primary data-[state=active]:shadow-none font-extrabold py-[17px] px-5">
							minmini Shop
						</TabsTrigger>
						<TabsTrigger
							value="kontakt"
							onClick={() => navigate('/kontakt')}
							className="cursor-pointer rounded-[48px] data-[state=active]:text-primary data-[state=active]:shadow-none font-extrabold py-[17px] px-5">
							Kontakt
						</TabsTrigger>
					</TabsList>
				</Tabs>

				<div className="hidden lg:flex lg:flex-1 lg:justify-end">
					{auth.type ? (
						<div className="flex items-center gap-5">
							<div className="flex items-center gap-4">
								<div
									onClick={() => dispatch(logout())}
									className="border cursor-pointer border-[#EEEFF0] size-10 flex items-center justify-center rounded-xl">
									<LogOutIcon className="size-5 shrink-0 rotate-180" />
								</div>
								<div
									onClick={() =>
										navigate(auth.type === 'admin' ? '/admin-panel' : '/customer/overview')
									}
									className="border cursor-pointer border-[#EEEFF0] size-10 flex items-center justify-center rounded-xl">
									<UserIcon className="size-5 shrink-0" />
								</div>
							</div>
						</div>
					) : (
						<Button>
							<Link to="/login">Anmelden</Link>
						</Button>
					)}
				</div>
			</nav>
			<Dialog open={mobileMenuOpen} onClose={setMobileMenuOpen} className="lg:hidden">
				<div className={cn('fixed inset-0 z-10', { 'bg-black/20': { mobileMenuOpen } })} />
				<DialogPanel className="fixed w-10/12 inset-y-0 left-0 z-10 overflow-y-auto bg-white px-6 py-10 sm:max-w-sm sm:ring-1 sm:ring-gray-900/10">
					<div className="flex items-center justify-between">
						<div className="-m-1.5 p-1.5 flex items-center gap-2">
							<a href="#" className="-m-1.5 p-1.5">
								<span className="sr-only">Book Stands</span>
								<LogoIcon className="h-8 w-auto" />
							</a>
							<div className="flex flex-col w-3/5 flex-wrap gap-1">
								<h3 className="text-secondary font-extrabold">minmini</h3>
								<p className="text-[10px] font-medium text-muted-foreground">
									(Ver)kaufe nachhaltig, schaffe Platz und verdiene dabei.
								</p>
							</div>
						</div>
						<button
							type="button"
							onClick={() => setMobileMenuOpen(false)}
							className="-m-2.5 rounded-md p-2.5 text-muted-foreground">
							<span className="sr-only">Close menu</span>
							<XMarkIcon aria-hidden="true" className="size-6" />
						</button>
					</div>
					<div className="mt-6 flow-root">
						<div className="-my-6 divide-y divide-gray-500/10">
							<div></div>
							<div className="space-y-2 py-6">
								<Link
									to="/"
									className="-mx-3 block rounded-lg px-3 py-2 text-sm hover:font-extrabold font-medium text-secondary hover:bg-gray-50">
									Startseite
								</Link>
								<a
									href="/customer/regal-mieten"
									className="-mx-3 block rounded-lg px-3 py-2 text-sm hover:font-extrabold font-medium text-secondary hover:bg-gray-50">
									Regal Mieten
								</a>
								<Link
									to="https://www.minmini-shop.de/"
									target="__blank"
									className="-mx-3 block rounded-lg px-3 py-2 text-sm hover:font-extrabold font-medium text-secondary hover:bg-gray-50">
									minmini Shop
								</Link>
								<Link
									to="/kontakt"
									className="-mx-3 block rounded-lg px-3 py-2.5 text-sm hover:font-extrabold font-medium text-secondary hover:bg-gray-50">
									Kontakt
								</Link>
								<Link
									to="/suchen"
									className="-mx-3 flex items-center gap-2 rounded-lg px-3 py-2 text-sm hover:font-extrabold font-medium text-secondary hover:bg-gray-50">
									<Search className="size-4" />
									Artikelsuche
								</Link>
							</div>
						</div>
					</div>
				</DialogPanel>
			</Dialog>
		</header>
	)
}
